// The profile's "Progress" page (mockups c-profile.png, e-profile-phone.png): /u/<name>/progress (and
// /u/<name>/achievements, the same page scrolled to the achievements). Data: GET /api/aethernal/progress/<id>
// (src/Gamify/Http/ProgressController.php). The member themself also gets the main-character picker (the website owns
// the choice), the privacy switches (Flarum user preferences aetGame / aetShowChip / aetShowGuild / aetShowCharName)
// and their other characters (only ever shown to them); moderators get the recent EXP log and the award buttons.
import app from 'flarum/forum/app';
import UserPage from 'flarum/forum/components/UserPage';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Button from 'flarum/common/components/Button';
import { t, ts, nf, api, fmtDate, ACH, ACH_TOTAL, Emblem, ExpBar, Chip, Flag, Crest, empName, jobName, classGlyph, classKind, MedalIcon, achName } from './ui';
const HOW = [['far fa-comment', 'reply', '+5'], ['far fa-pen-to-square', 'discussion', '+15'], ['far fa-thumbs-up', 'like', '+3'], ['fas fa-book-open', 'guide', '+150'], ['far fa-calendar-check', 'visit', '+2'], ['fas fa-medal', 'achievement', '+10 – +150']];
function ago(unix) {
  const s = Math.max(0, Date.now() / 1000 - unix);
  const loc = app.data.locale || 'en';
  try {
    const rtf = new Intl.RelativeTimeFormat(loc, {
      numeric: 'auto',
      style: 'narrow'
    });
    if (s < 3600) return rtf.format(-Math.max(1, Math.round(s / 60)), 'minute');
    if (s < 86400) return rtf.format(-Math.round(s / 3600), 'hour');
    return rtf.format(-Math.round(s / 86400), 'day');
  } catch (e) {
    return fmtDate(unix);
  }
}
function Toggle(label, on, onchange, disabled = false, name = '') {
  return m("label", {
    className: `aet-toggle${on ? ' is-on' : ''}${disabled ? ' is-disabled' : ''}`
  }, m("span", {
    className: "aet-toggle__label"
  }, label), m("input", {
    type: "checkbox",
    role: "switch",
    name: name,
    checked: on,
    disabled: disabled,
    onchange: e => onchange(e.target.checked)
  }), m("s", {
    "aria-hidden": "true"
  }), m("span", {
    className: "aet-toggle__state",
    "aria-hidden": "true"
  }, on ? t('on') : t('off')));
}
export default class ProgressPage extends UserPage {
  oninit(vnode) {
    super.oninit(vnode);
    this.data = null;
    this.filter = 'all';
    this.showAll = false;
    this.saving = false;
    this.notice = null;
    this.focus = vnode.attrs.focus || (m.route.get() || '').endsWith('/achievements') ? 'achievements' : null;
    this.loadUser(m.route.param('username'));
  }
  show(user) {
    super.show(user);
    this.load();
  }
  load() {
    return api('GET', `/aethernal/progress/${this.user.id()}`).then(d => {
      this.data = d;
      m.redraw();
      if (this.focus === 'achievements') {
        this.focus = null;
        setTimeout(() => {
          const el = document.getElementById('aet-achievements');
          if (el) el.scrollIntoView({
            block: 'start'
          });
        }, 50);
      }
    });
  }

  /** Reload the progress and the user (posts show the new chip without a page reload). */
  reload() {
    return Promise.all([this.load(), app.store.find('users', this.user.id())]).then(() => m.redraw());
  }
  content() {
    const d = this.data;
    if (!d) return m(LoadingIndicator, null);
    return m("div", {
      className: "aet-progress"
    }, this.progressCard(d), m("div", {
      className: "aet-two aet-two--earn"
    }, this.howCard(), d.log ? this.logCard(d) : null), d.game && d.self ? m("div", {
      className: "aet-two"
    }, this.mainCard(d), this.othersCard(d)) : d.game && d.game.main ? m("div", {
      className: "aet-two"
    }, this.publicMainCard(d.game.main)) : null, this.achievementsCard(d));
  }
  progressCard(d) {
    const g = d.level;
    const max = g.lv >= 50;
    return m("section", {
      className: "aet-card aet-card--progress"
    }, m("h3", null, m("i", {
      className: "icon fas fa-chart-line",
      "aria-hidden": "true"
    }), t('progress_title'), m("span", {
      className: "aet-card__aside"
    }, ts('lifetime', {
      exp: nf(g.exp)
    }))), m("div", {
      className: "aet-prog"
    }, m("div", null, Emblem(g, 'xl')), m("div", {
      className: "aet-prog__main"
    }, m("div", {
      className: "aet-prog__t"
    }, m("small", null, ts(`tier.${g.tier}`)), ts('level_n', {
      level: g.lv
    })), ExpBar(g.pct, 'lg'), m("div", {
      className: "aet-prog__info"
    }, m("span", null, max ? t('max_level') : m("span", null, m("b", null, nf(g.into)), " / ", nf(g.need), " ", t('exp'))), max ? null : m("span", null, ts('to_level', {
      count: nf(g.toNext),
      level: g.lv + 1
    }))))));
  }
  howCard() {
    return m("section", {
      className: "aet-card aet-card--how"
    }, m("h3", null, m("i", {
      className: "icon fas fa-bolt",
      "aria-hidden": "true"
    }), t('how_title')), m("table", {
      className: "aet-earn"
    }, m("tbody", null, HOW.map(([icon, key, v]) => m("tr", null, m("td", null, m("i", {
      className: `icon ${icon}`,
      "aria-hidden": "true"
    }), t(`how.${key}`)), m("td", null, v))))), m("p", {
      className: "aet-note"
    }, m("i", {
      className: "icon fas fa-shield-halved",
      "aria-hidden": "true"
    }), m("span", null, t('how_cap'))));
  }
  logText(e) {
    if (e.kind === 'like') return ts('log.like', {
      count: nf(e.count)
    });
    if (e.kind === 'visit') return ts('log.visit');
    if (e.kind === 'achievement') return ts('log.achievement', {
      name: ACH[e.achievement] ? achName(e.achievement) : e.achievement
    });
    if (['reply', 'discussion', 'guide'].includes(e.kind)) return e.title ? ts(`log.${e.kind}`, {
      title: e.title
    }) : ts(`log.${e.kind}_plain`);
    return e.kind;
  }
  logCard(d) {
    return m("section", {
      className: "aet-card aet-card--log"
    }, m("h3", null, m("i", {
      className: "icon fas fa-clock-rotate-left",
      "aria-hidden": "true"
    }), t('recent_title')), d.log.length ? m("ul", {
      className: "aet-log"
    }, d.log.map(e => m("li", null, m("b", null, "+", nf(e.amount)), m("span", {
      className: "aet-log__what"
    }, this.logText(e)), m("span", {
      className: "aet-log__when"
    }, ago(e.at))))) : m("p", {
      className: "aet-note"
    }, t('log.empty')), m("p", {
      className: "aet-note"
    }, m("i", {
      className: "icon fas fa-circle-info",
      "aria-hidden": "true"
    }), m("span", null, t('cap_note'))));
  }
  savePref(key, value) {
    this.saving = true;
    return this.user.savePreferences({
      [key]: value
    }).then(() => this.reload()).finally(() => {
      this.saving = false;
      m.redraw();
    });
  }
  pick(value) {
    this.saving = true;
    this.notice = null;
    const player = value === '' ? null : parseInt(value, 10);
    return api('POST', '/aethernal/gamify/main', {
      player
    }).then(() => {
      this.notice = ['ok', ts('pick_saved')];
      return this.reload();
    }, () => {
      this.notice = ['err', ts('pick_failed')];
    }).finally(() => {
      this.saving = false;
      m.redraw();
    });
  }
  portrait(c, size = '') {
    return m("div", {
      className: `aet-portrait ${size} aet-cls--${classKind(c.job)}`
    }, classGlyph(c.job, size ? 28 : 46), size ? null : m("span", {
      className: "aet-portrait__lv"
    }, ts('lv', {
      level: c.lv
    })));
  }
  mainCard(d) {
    const g = d.game;
    const p = g.prefs;
    const c = g.main;
    const off = !p.aetGame;
    return m("section", {
      className: "aet-card aet-card--main"
    }, m("h3", null, m("i", {
      className: "icon fas fa-user-shield",
      "aria-hidden": "true"
    }), t('main_title'), m("span", {
      className: "aet-card__aside"
    }, t('from_account'))), off ? m("p", {
      className: "aet-note"
    }, t('game_off')) : c ? m("div", {
      className: "aet-cc"
    }, this.portrait(c), m("div", {
      className: "aet-cc__info"
    }, m("h4", null, c.name), m("div", {
      className: "aet-cc__sub"
    }, m("span", null, jobName(c.job)), c.emp ? [m("span", {
      className: "aet-chip__dot"
    }, "\xB7"), Flag(c.emp), m("span", null, empName(c.emp))] : null), c.guild ? m("dl", {
      className: "aet-kv"
    }, m("dt", null, t('guild')), m("dd", null, Crest(c.guild), " ", c.guild.name)) : null)) : m("p", {
      className: "aet-note"
    }, g.fetchedAt ? t('no_chars') : t('game_wait')), !off && g.chars.length > 1 ? m("div", {
      className: "aet-pick"
    }, m("label", {
      for: "aet-main-pick"
    }, t('pick_label')), m("select", {
      id: "aet-main-pick",
      className: "FormControl",
      disabled: this.saving,
      onchange: e => this.pick(e.target.value)
    }, m("option", {
      value: "",
      selected: g.choice === null
    }, ts('pick_auto')), g.chars.map(ch => m("option", {
      value: String(ch.id),
      selected: g.choice === ch.id
    }, `${ch.name} · ${ts('lv', {
      level: ch.lv
    })} · ${jobName(ch.job)}`))), m("p", {
      className: "aet-pick__help"
    }, t('pick_help')), this.notice ? m("p", {
      className: `aet-pick__notice aet-pick__notice--${this.notice[0]}`,
      role: "status"
    }, this.notice[1]) : null) : null, m("div", {
      className: "aet-priv"
    }, Toggle(t('priv_master'), p.aetGame, v => this.savePref('aetGame', v), this.saving, 'aetGame'), Toggle(t('priv_chip'), p.aetShowChip, v => this.savePref('aetShowChip', v), this.saving || off, 'aetShowChip'), Toggle(t('priv_guild'), p.aetShowGuild, v => this.savePref('aetShowGuild', v), this.saving || off, 'aetShowGuild'), Toggle(t('priv_name'), p.aetShowCharName, v => this.savePref('aetShowCharName', v), this.saving || off, 'aetShowCharName')), !off && g.public ? m("div", {
      className: "aet-priv__preview"
    }, m("span", {
      className: "aet-priv__preview-label"
    }, t('shown_as')), Chip(g.public)) : null);
  }
  othersCard(d) {
    const g = d.game;
    const others = g.prefs.aetGame ? g.chars.filter(c => !g.main || c.id !== g.main.id) : [];
    return m("section", {
      className: "aet-card aet-card--others"
    }, m("h3", null, m("i", {
      className: "icon fas fa-users",
      "aria-hidden": "true"
    }), t('other_title'), m("span", {
      className: "aet-card__aside"
    }, m("span", {
      className: "aet-pill"
    }, m("i", {
      className: "icon fas fa-lock",
      "aria-hidden": "true"
    }), t('only_you')))), others.length ? others.map(c => m("div", {
      className: "aet-alt"
    }, m("span", {
      className: `aet-alt__cls aet-cls--${classKind(c.job)}`
    }, classGlyph(c.job, 17)), m("div", null, m("div", {
      className: "aet-alt__nm"
    }, c.name), m("div", {
      className: "aet-alt__meta"
    }, jobName(c.job), " \xB7 ", ts('lv', {
      level: c.lv
    }), c.emp ? ` · ${empName(c.emp)}` : '')), m("span", {
      className: "aet-alt__eye"
    }, m("i", {
      className: "icon fas fa-eye-slash",
      "aria-hidden": "true"
    }), t('hidden')))) : m("p", {
      className: "aet-note"
    }, g.prefs.aetGame ? t('no_chars') : t('game_off')), m("p", {
      className: "aet-note"
    }, m("i", {
      className: "icon fas fa-circle-info",
      "aria-hidden": "true"
    }), m("span", null, t('priv_note'))));
  }
  publicMainCard(c) {
    return m("section", {
      className: "aet-card aet-card--main"
    }, m("h3", null, m("i", {
      className: "icon fas fa-user-shield",
      "aria-hidden": "true"
    }), t('main_title'), m("span", {
      className: "aet-card__aside"
    }, t('from_game'))), m("div", {
      className: "aet-cc"
    }, this.portrait(c), m("div", {
      className: "aet-cc__info"
    }, c.name ? m("h4", null, c.name) : null, Chip({
      ...c,
      name: undefined
    }), m("dl", {
      className: "aet-kv"
    }, c.emp ? [m("dt", null, t('kingdom')), m("dd", null, Flag(c.emp), " ", empName(c.emp))] : null, c.guild ? [m("dt", null, t('guild')), m("dd", null, Crest(c.guild), " ", c.guild.name)] : null))));
  }
  award(key, on) {
    this.saving = true;
    return api('POST', '/aethernal/gamify/award', {
      user: this.user.id(),
      key,
      on
    }).then(() => this.reload()).finally(() => {
      this.saving = false;
      m.redraw();
    });
  }
  tile(a, d) {
    const on = a.unlocked !== null;
    const r = a.rarity;
    let foot;
    if (on) {
      foot = [m("span", null, a.source === 'staff' ? t('awarded') : ts('unlocked_on', {
        date: fmtDate(a.unlocked, {
          day: 'numeric',
          month: 'short'
        })
      })), m("span", null, "+", a.exp, " ", t('exp'))];
    } else if (!a.available) {
      foot = [m("span", null, t('not_available')), m("span", null, "+", a.exp, " ", t('exp'))];
    } else if (a.progress) {
      foot = [m("span", {
        className: "aet-num"
      }, nf(a.progress[0]), " / ", nf(a.progress[1])), m("span", null, "+", a.exp, " ", t('exp'))];
    } else {
      foot = [m("span", null, a.best ? ts('best_rank', {
        rank: a.best
      }) : t(`ach.${a.key}.rule`)), m("span", null, "+", a.exp, " ", t('exp'))];
    }
    const awardable = d.canManage && (a.how === 'staff' || a.how === 'order');
    return m("div", {
      className: `aet-atile aet-r-${r} ${on ? 'is-on' : 'is-off'}`,
      "data-key": a.key
    }, m("span", {
      className: "aet-atile__rar"
    }, ts(`rarity.${r}`)), MedalIcon(a.key, `aet-medal--lg${on ? '' : ' is-locked'}`), m("h5", null, achName(a.key)), m("p", null, t(`ach.${a.key}.desc`)), !on && a.progress ? m("div", {
      className: "aet-mini"
    }, m("i", {
      style: {
        width: `${Math.min(100, a.progress[0] * 100 / a.progress[1])}%`
      }
    })) : null, m("div", {
      className: "aet-atile__foot"
    }, foot), awardable ? m(Button, {
      className: "Button Button--link aet-atile__manage",
      disabled: this.saving,
      onclick: () => this.award(a.key, !on)
    }, on ? t('manage_revoke') : t('manage_award')) : null);
  }
  achievementsCard(d) {
    const list = d.achievements.filter(a => this.filter === 'all' || a.group === this.filter);
    const unlocked = d.achievements.filter(a => a.unlocked !== null).length;
    const phone = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
    // unlocked first (newest first), then the locked ones in their order
    const sorted = list.filter(a => a.unlocked !== null).sort((a, b) => b.unlocked - a.unlocked).concat(list.filter(a => a.unlocked === null));
    const shown = phone && !this.showAll ? sorted.slice(0, 6) : sorted;
    const chip = (key, label) => m("button", {
      type: "button",
      className: `aet-fil__b${this.filter === key ? ' is-on' : ''}`,
      "aria-pressed": this.filter === key ? 'true' : 'false',
      onclick: () => this.filter = key
    }, label);
    return m("section", {
      className: "aet-card aet-card--ach",
      id: "aet-achievements"
    }, m("h3", null, m("i", {
      className: "icon fas fa-medal",
      "aria-hidden": "true"
    }), t('achievements'), m("span", {
      className: "aet-card__aside aet-num"
    }, ts('n_of', {
      count: unlocked,
      total: d.achievements.length
    }))), m("div", {
      className: "aet-fil",
      role: "group"
    }, chip('all', t('filter_all')), chip('forum', t('group.forum')), chip('game', t('group.game')), chip('event', t('group.event'))), m("div", {
      className: "aet-agrid"
    }, shown.map(a => this.tile(a, d))), shown.length < sorted.length ? m("p", {
      className: "aet-note aet-note--center"
    }, m(Button, {
      className: "Button",
      onclick: () => this.showAll = true
    }, ts('show_all', {
      total: sorted.length
    }))) : null);
  }
}
export { ACH_TOTAL };
flarum.reg.add('aethernal-forum', 'forum/gamify/ProgressPage', ProgressPage);